import { joinTimeMillis } from '@/lib/notifications/waitingRoomArrivals';

/**
 * When a waiting beneficiary should follow `waitingRooms/{id}.activeCall` into
 * the call (#47).
 *
 * Two shapes are in the field:
 * - Legacy (every client today): `{customerId, roomId, startTime}`, with
 *   `startTime` stamped by the doctor's clock. Accept if it is for me and less
 *   than 60 s old by MY clock — two-device clock skew can break this, which is
 *   why the new shape exists.
 * - New (mobile/functions rollout): also carries `callId`, and may carry the
 *   `joinTime` of the presence doc the doctor called. Accept by identity, not
 *   by clock: a callId I have not handled yet, and — when `joinTime` is
 *   present — only if it is my current presence session.
 *
 * In both shapes a call I already handled (joined, then came back to the
 * waiting room) is never accepted again — this breaks the re-entry loop.
 */

export interface ActiveCallSignal {
  customerId?: string;
  roomId?: string;
  callId?: string;
  startTime?: unknown;
  joinTime?: unknown;
}

export const LEGACY_ACTIVE_CALL_MAX_AGE_MS = 60_000;
/** Tolerance when matching the call's joinTime to my presence joinTime. */
const JOIN_TIME_MATCH_MS = 1_000;

export function activeCallKey(call: ActiveCallSignal): string | null {
  return call.callId || call.roomId || null;
}

export interface AcceptanceContext {
  customerId: string;
  nowMs: number;
  handled: ReadonlySet<string>;
  /** joinTime of my own presence doc, if known. */
  myJoinTimeMs?: number | null;
}

export function shouldAcceptActiveCall(
  call: ActiveCallSignal | null | undefined,
  ctx: AcceptanceContext
): boolean {
  if (!call || !ctx.customerId || call.customerId !== ctx.customerId || !call.roomId) {
    return false;
  }
  const key = activeCallKey(call);
  if (!key || ctx.handled.has(key)) return false;

  if (call.callId) {
    const callJoinMs = joinTimeMillis(call.joinTime);
    if (callJoinMs != null && ctx.myJoinTimeMs != null) {
      return Math.abs(callJoinMs - ctx.myJoinTimeMs) <= JOIN_TIME_MATCH_MS;
    }
    return true;
  }

  const startMs = joinTimeMillis(call.startTime);
  if (startMs == null) return false;
  return ctx.nowMs - startMs < LEGACY_ACTIVE_CALL_MAX_AGE_MS;
}

/**
 * Calls this tab already followed. Module scope on purpose: it must survive the
 * WaitingRoom remounting when a failed call sends the beneficiary back.
 */
const handledActiveCalls = new Set<string>();

export function getHandledActiveCalls(): ReadonlySet<string> {
  return handledActiveCalls;
}

export function markActiveCallHandled(call: ActiveCallSignal): void {
  const key = activeCallKey(call);
  if (key) handledActiveCalls.add(key);
}
